import { useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@repo/ui/components/Base/Dialog";
import { type CapturedCancel, MeetingForm } from "./MeetingForm";
import { Tooltip } from "@repo/ui/components/Base/Tooltip";
import { MEETING_MOTIF } from "@/lib/constants";
import type { Meeting } from "@repo/types";
import { useNavigate } from "react-router-dom";
import { AppUrlPath } from "@/lib/AppUrlPath";
import { CreateTriggerButton } from "@repo/ui/components/Base/CreateTriggerButton";

export const CreateMeetingDialog = ({
  tenantId,
  disabled,
  open,
  setOpen,
}: {
  tenantId: string;
  disabled?: boolean;
  open?: boolean;
  setOpen?: (open: boolean) => void;
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const capturedCancel = useRef<CapturedCancel>({
    close() {
      setOpen ? setOpen(false) : setInternalOpen(false);
    },
  });

  const navigate = useNavigate();

  const handleOpenChange = (newOpen: boolean) => {
    if (
      (open ? open : internalOpen) &&
      !newOpen &&
      capturedCancel.current.cancel
    ) {
      // if trying to close, call capturedCancel.cancel instead
      capturedCancel.current.cancel();
    } else {
      setOpen ? setOpen(newOpen) : setInternalOpen(newOpen);
    }
  };

  const handleCreated = (meeting: Meeting) => {
    const url = new AppUrlPath(tenantId).getMeetingPath(meeting.id);
    navigate(url);
  };

  return (
    <Dialog open={open ? open : internalOpen} onOpenChange={handleOpenChange}>
      <Tooltip
        label={
          disabled ? "Select a team to create a meeting" : "Create a Meeting"
        }
        tooltipTriggerProps={{ disabled: disabled }}
        asChild
      >
        {open === undefined && (
          <DialogTrigger asChild>
            <CreateTriggerButton disabled={disabled} />
          </DialogTrigger>
        )}
      </Tooltip>
      <DialogContent preventClickout preventEscape>
        <DialogHeader className={MEETING_MOTIF}>
          <DialogTitle>Create A Meeting</DialogTitle>
        </DialogHeader>
        <MeetingForm
          tenantId={tenantId}
          handleCancel={() => handleOpenChange(false)}
          handleComplete={() => handleOpenChange(false)}
          onSubmit={handleCreated}
          capturedCancel={capturedCancel.current}
          hidePreview
          withinDialog
        />
      </DialogContent>
    </Dialog>
  );
};
